import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { BarChart3, User, Save, ArrowLeft, TrendingUp, Sparkles, Trophy, Target, FileText, Settings } from 'lucide-react'
import MoodSelector from './components/MoodSelector'
import NoteInput from './components/NoteInput'
import History from './components/History'
import Registration from './components/Registration'
import SuccessAnimation from './components/SuccessAnimation'
import Reports from './components/Reports'
import Achievements from './components/Achievements'
// import Mascot from './components/Mascot'
import PWAPrompt from './components/PWAPrompt'
import NotificationSettings from './components/NotificationSettings'
import { MoodEntry, User as UserType, Achievement, Challenge } from './types'
import { saveMoodEntry, getMoodEntries, saveUser, getUser, getAchievements, saveAchievement, getChallenges, saveChallenge } from './utils/storage'
import { checkAchievements, checkChallenges } from './utils/gamification'
import { useNotifications } from './hooks/useNotifications'

type View = 'mood' | 'note' | 'history' | 'register' | 'success' | 'reports' | 'achievements' | 'settings'

function App() {
  const [currentView, setCurrentView] = useState<View>('mood')
  const [selectedMood, setSelectedMood] = useState<number | null>(null)
  const [entries, setEntries] = useState<MoodEntry[]>([])
  const [user, setUser] = useState<UserType | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [challenges, setChallenges] = useState<Challenge[]>([])
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([])
  
  const { sendNotification } = useNotifications()

  useEffect(() => {
    setEntries(getMoodEntries())
    setUser(getUser())
    setAchievements(getAchievements())
    setChallenges(getChallenges())

    // Handle PWA shortcuts
    const shortcutAction = localStorage.getItem('pwa-shortcut-action')
    if (shortcutAction) {
      localStorage.removeItem('pwa-shortcut-action')
      switch (shortcutAction) {
        case 'history':
          setCurrentView('history')
          break
        case 'achievements':
          setCurrentView('achievements')
          break
        case 'quick-entry':
          // Stay on mood view for quick entry
          break
      }
    }
  }, [])

  const handleMoodSelect = (mood: number) => {
    setSelectedMood(mood)
    setCurrentView('note')
  }

  const handleSaveEntry = (note: string) => {
    if (selectedMood !== null) {
      const entry: MoodEntry = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        mood: selectedMood,
        note: note.trim()
      }
      
      saveMoodEntry(entry)
      const updatedEntries = getMoodEntries()
      setEntries(updatedEntries)
      
      // Check for new achievements and challenges
      const newAchievs = checkAchievements(updatedEntries, achievements)
      const updatedChallenges = checkChallenges(updatedEntries, challenges)
      
      if (newAchievs.length > 0) {
        newAchievs.forEach(achievement => saveAchievement(achievement))
        setAchievements(getAchievements())
        setNewAchievements(newAchievs)
        
        // Send achievement notification
        sendNotification('🏆 Nuovo Achievement!', {
          body: `Hai sbloccato: ${newAchievs[0].title}`,
          tag: 'achievement'
        })
      }
      
      setChallenges(updatedChallenges)
      updatedChallenges.forEach(challenge => saveChallenge(challenge))
      
      // Check for completed challenges
      const completedChallenges = updatedChallenges.filter(c => c.completed && !challenges.find(old => old.id === c.id && old.completed))
      if (completedChallenges.length > 0) {
        sendNotification('🎯 Sfida Completata!', {
          body: `Hai completato: ${completedChallenges[0].title}`,
          tag: 'challenge'
        })
      }
      
      setSelectedMood(null)
      setShowSuccess(true)
      setCurrentView('success')
      
      setTimeout(() => {
        setShowSuccess(false)
        setCurrentView('mood')
        if (newAchievs.length > 0) {
          setTimeout(() => setCurrentView('achievements'), 500)
        }
      }, 2500)
    }
  }

  const handleRegister = (email: string, password: string) => {
    const newUser: UserType = { email, password }
    saveUser(newUser)
    setUser(newUser)
    setCurrentView('mood')
  }

  const goBack = () => {
    if (currentView === 'note') {
      setCurrentView('mood')
      setSelectedMood(null)
    } else {
      setCurrentView('mood')
    }
  }

  const hasEntryToday = () => {
    const today = new Date().toDateString()
    return entries.some(entry => new Date(entry.date).toDateString() === today)
  }

  const getCurrentStreak = () => {
    let streak = 0
    const today = new Date()
    
    for (let i = 0; i < 365; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() - i)
      const hasEntry = entries.some(entry => 
        new Date(entry.date).toDateString() === checkDate.toDateString()
      )
      
      if (hasEntry) {
        streak++
      } else if (i > 0) {
        break
      }
    }
    
    return streak
  }

  const getAverageMood = () => {
    if (entries.length === 0) return 0
    const sum = entries.reduce((acc, entry) => acc + entry.mood, 0)
    return Number((sum / entries.length).toFixed(1))
  }

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -20 }
  }

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.3
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      <PWAPrompt />
      
      <div className="max-w-md mx-auto bg-white/80 backdrop-blur-sm min-h-screen shadow-2xl border-x border-white/20 relative">
        {/* Mascot 
        <Mascot 
          mood={getAverageMood()} 
          streak={getCurrentStreak()}
          hasEntryToday={hasEntryToday()}
          newAchievements={newAchievements}
          onAchievementClick={() => setCurrentView('achievements')}
        />*/}

        {/* Header */}
        <motion.header 
          className="bg-white/90 backdrop-blur-md border-b border-purple-100 px-4 py-4 sticky top-0 z-20"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between">
            {currentView !== 'mood' && currentView !== 'success' && (
              <motion.button
                onClick={goBack}
                className="p-2 hover:bg-black-100 rounded-full transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ArrowLeft className="w-5 h-5 text-black-600" />
              </motion.button>
            )}
            
            <motion.h1 
              className="text-lg font-bold bg-black bg-clip-text text-transparent flex-1 flex gap-2"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {currentView === 'mood' && (
                <>
                  <img className="h-10" src='https://app.gitaigo.it/gitaigo.svg' />
                </>
              )}
              {currentView === 'note' && 'Aggiungi Nota'}
              {currentView === 'history' && 'Il Tuo Viaggio'}
              {currentView === 'register' && 'Unisciti a Noi'}
              {currentView === 'reports' && 'Report Mensile'}
              {currentView === 'achievements' && 'Achievements'}
              {currentView === 'settings' && 'Impostazioni'}
            </motion.h1>

            {currentView === 'mood' && (
              <div className="flex space-x-1">
                <motion.button
                  onClick={() => setCurrentView('settings')}
                  className="p-2 hover:bg-black-100 rounded-full transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Settings className="w-5 h-5 text-black-600" />
                </motion.button>
                <motion.button
                  onClick={() => setCurrentView('achievements')}
                  className="p-2 hover:bg-black-100 rounded-full transition-colors relative"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Trophy className="w-5 h-5 text-black-600" />
                  {newAchievements.length > 0 && (
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}
                </motion.button>
                <motion.button
                  onClick={() => setCurrentView('reports')}
                  className="p-2 hover:bg-black-100 rounded-full transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FileText className="w-5 h-5 text-black-600" />
                </motion.button>
                <motion.button
                  onClick={() => setCurrentView('history')}
                  className="p-2 hover:bg-black-100 rounded-full transition-colors relative"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <BarChart3 className="w-5 h-5 text-black-600" />
                  {entries.length > 0 && (
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-pink-500 rounded-full"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}
                </motion.button>
                {!user && (
                  <motion.button
                    onClick={() => setCurrentView('register')}
                    className="p-2 hover:bg-black-100 rounded-full transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <User className="w-5 h-5 text-black-600" />
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </motion.header>

        {/* Main Content */}
        <main className="p-4 pb-20 relative overflow-hidden">
          <AnimatePresence mode="wait">
            {currentView === 'mood' && (
              <motion.div
                key="mood"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
                className="space-y-6"
              >
                {/*hasEntryToday() && (
                  <motion.div 
                    className="bg-gradient-to-r from-purple-50 to-emerald-50 border border-purple-200 rounded-2xl p-4 shadow-lg"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="flex items-center space-x-3">
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
                      >
                        <Save className="w-6 h-6 text-purple-600" />
                      </motion.div>
                      <div>
                        <span className="text-purple-800 font-semibold">
                          Fantastico! 🎉
                        </span>
                        <p className="text-purple-700 text-sm">
                          Hai già registrato il tuo umore oggi!
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )*/}
                
                <MoodSelector onMoodSelect={handleMoodSelect} />
                
                {/* Quick Stats */}
                {entries.length > 0 && (
                  <motion.div 
                    className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 shadow-lg border border-purple-100"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-purple-600">{getCurrentStreak()}</div>
                        <div className="text-xs text-purple-500">Streak</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-pink-600">{getAverageMood()}</div>
                        <div className="text-xs text-pink-500">Media</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-indigo-600">{achievements.filter(a => a.unlocked).length}</div>
                        <div className="text-xs text-indigo-500">Trofei</div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Active Challenges */}
                {challenges.filter(c => c.active && !c.completed).length > 0 && (
                  <motion.div 
                    className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 shadow-lg border border-yellow-200"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <Target className="w-5 h-5 text-orange-600" />
                      <h3 className="font-bold text-orange-800">Sfida Attiva</h3>
                    </div>
                    {challenges.filter(c => c.active && !c.completed).slice(0, 1).map(challenge => (
                      <div key={challenge.id} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-orange-700 font-medium">{challenge.title}</span>
                          <span className="text-orange-600 text-sm">{challenge.progress}/{challenge.target}</span>
                        </div>
                        <div className="w-full bg-orange-200 rounded-full h-2">
                          <motion.div
                            className="bg-orange-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${(challenge.progress / challenge.target) * 100}%` }}
                            transition={{ duration: 1, delay: 0.8 }}
                          />
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )}

            {currentView === 'note' && selectedMood !== null && (
              <motion.div
                key="note"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <NoteInput
                  selectedMood={selectedMood}
                  onSave={handleSaveEntry}
                  onSkip={() => handleSaveEntry('')}
                />
              </motion.div>
            )}

            {currentView === 'history' && (
              <motion.div
                key="history"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <History entries={entries} />
              </motion.div>
            )}

            {currentView === 'reports' && (
              <motion.div
                key="reports"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <Reports entries={entries} />
              </motion.div>
            )}

            {currentView === 'achievements' && (
              <motion.div
                key="achievements"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <Achievements 
                  achievements={achievements} 
                  challenges={challenges}
                  entries={entries}
                  onNewAchievementsSeen={() => setNewAchievements([])}
                />
              </motion.div>
            )}

            {currentView === 'settings' && (
              <motion.div
                key="settings"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <NotificationSettings />
              </motion.div>
            )}

            {currentView === 'register' && (
              <motion.div
                key="register"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <Registration onRegister={handleRegister} />
              </motion.div>
            )}

            {currentView === 'success' && (
              <motion.div
                key="success"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <SuccessAnimation />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* User Status */}
        {user && currentView === 'mood' && (
          <motion.div 
            className="fixed bottom-4 left-4 right-4 max-w-md mx-auto"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-3 shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="text-blue-800 text-sm font-medium">
                    Benvenuto, {user.email.split('@')[0]}! 👋
                  </span>
                  <p className="text-blue-600 text-xs">
                    I tuoi dati sono al sicuro
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Floating Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-20 h-20 bg-purple-200/20 rounded-full blur-xl"
            animate={{
              y: [0, -20, 0],
              x: [0, 10, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute top-40 right-10 w-16 h-16 bg-pink-200/20 rounded-full blur-xl"
            animate={{
              y: [0, 20, 0],
              x: [0, -10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-40 left-20 w-12 h-12 bg-blue-200/20 rounded-full blur-xl"
            animate={{
              y: [0, -15, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default App